"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Button } from "./ui/button";
import { Sparkles, TrendingUp, Users, BarChart3 } from "lucide-react";

export const HeroSection = () => {
  const [prompt, setPrompt] = useState("");
  const [textIndex, setTextIndex] = useState(0);
  const router = useRouter();

  const animatedTexts = [
    "Join founders",
    "From all over the world",
    "Building",
    "Growing",
    "Together"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setTextIndex((prev) => (prev + 1) % animatedTexts.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const handlePromptSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Redirect to signup page
    router.push("/signup");
  };

  return (
    <section className="min-h-screen flex">
      {/* Left Section - 50% with overlay for more space */}
      <div className="w-full lg:w-[50%] flex flex-col justify-center px-4 sm:px-6 md:px-12 lg:px-20 py-12 relative z-20">
        {/* Gradient Glow Effect */}
        <div className="absolute top-1/4 left-0 w-[60vw] sm:left-1/4 sm:w-[80vw] max-w-96 h-[60vw] sm:h-[80vw] max-h-96 bg-gradient-glow opacity-50 blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-3xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border mb-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm text-muted-foreground">Growth Analytics for Lovable + Supabase Apps</span>
          </div>

          {/* Headline */}
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-100">
            Accelerate Your App's{" "}
            <span className="bg-gradient-primary bg-clip-text text-transparent">
              Growth
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-muted-foreground mb-12 leading-relaxed animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
            Deep analytics, user behavior insights, and funnel optimization for apps built with Lovable and Supabase.
            Connect securely in seconds, disconnect anytime—you're always in control.
          </p>

          {/* Prompt Input */}
          <form onSubmit={handlePromptSubmit} className="mb-12 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300">
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-primary rounded-2xl opacity-50 group-hover:opacity-75 blur transition duration-300" />
              <div className="relative bg-card rounded-2xl p-2 border border-border">
                <input
                  type="text"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Ask about your growth metrics, user behavior, or funnel analysis..."
                  className="w-full bg-transparent text-foreground placeholder:text-muted-foreground px-4 py-4 focus:outline-none text-lg"
                />
                <div className="flex items-center justify-between mt-2 px-2">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-1 rounded-md hover:bg-muted"
                    >
                      + Attach
                    </button>
                  </div>
                  <Button type="submit" variant="hero" size="sm">
                    Analyze
                  </Button>
                </div>
              </div>
            </div>
          </form>

          {/* Feature Pills */}
          <div className="flex flex-wrap gap-4 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-500">
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-card/50 backdrop-blur-sm border border-border/50">
              <TrendingUp className="w-4 h-4 text-secondary" />
              <span className="text-sm">Growth Metrics</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-card/50 backdrop-blur-sm border border-border/50">
              <Users className="w-4 h-4 text-accent" />
              <span className="text-sm">User Behavior</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-card/50 backdrop-blur-sm border border-border/50">
              <BarChart3 className="w-4 h-4 text-primary" />
              <span className="text-sm">Funnel Analysis</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Section - 60% Video Background */}
      <div className="hidden lg:block w-[60%] lg:ml-[-10%] relative overflow-hidden">
        {/* Video Overlay Gradient - fade over 35% of page width (50% of video section) */}
        <div className="absolute inset-0 bg-gradient-to-l from-transparent from-50% to-background z-10 pointer-events-none" />

        {/* Animated Text Overlay */}
        <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
          <div className="text-center px-8">
            <h2
              key={textIndex}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white animate-in fade-in slide-in-from-bottom-6 duration-500"
            >
              {animatedTexts[textIndex]}
            </h2>
          </div>
        </div>

        {/* Background Video */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/videos/hero-background.mp4" type="video/mp4" />
        </video>
      </div>
    </section>
  );
};
